import type { CrossPlatformHashFunction } from '~~/common/config/CrossPlatformHashFunction';
import { HASH_SHA_256, HASH_SHA_512 } from '~~/common/config/CrossPlatformHashFunction';
import { uuid4 as uuid4Original } from '~~/common/utils/uuid';

const numberFormatters: Record<string, Intl.NumberFormat | undefined> = {};

export const uuid4 = uuid4Original;

export function uuid4FormData (
  formData: Record<string, {
    labelAttrs: {
      for: string,
    },
    widgetAttrs: {
      id: string,
    },
  }>,
  postfix = uuid4(),
) {
  Object.values(formData).forEach(({ labelAttrs, widgetAttrs }) => {
    labelAttrs.for = labelAttrs.for + postfix;
    widgetAttrs.id = widgetAttrs.id + postfix;
  });
}

export function epsRound (x: number) {
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

/* https://stackoverflow.com/questions/54246477/how-to-convert-camelcase-to-snake-case-in-javascript */
export function camelToSnakeCase (str: string) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

type UrlObj = { url: string };

export function webpFallback (baseSources?: (UrlObj | null | undefined)[]) {
  const urlObjSources = (Array.isArray(baseSources) ? baseSources : []).filter(isUrlObjGuard);
  const MIME_WEBP = 'image/webp';
  const getMimeType = (url: string) => url.endsWith('.webp') ? { mimeType: MIME_WEBP } : null;
  const mimeTypedSources = urlObjSources
    .map(bs => ({
      ...bs,
      ...getMimeType(bs.url),
    }))
  ;
  return [
    ...(mimeTypedSources
      .filter(({ mimeType }) => {
        return mimeType !== MIME_WEBP;
      })
      .map(s => ({
        ...s,
        url: `${s.url}.webp`,
        mimeType: MIME_WEBP,
      }))
    ),
    ...mimeTypedSources,
  ];

  function isUrlObjGuard (x: unknown): x is UrlObj {
    if (typeof x !== 'object' || !x) {
      return false;
    }
    const y = x as Record<string, unknown>;
    return !!(y.url && typeof y.url === 'string');
  }
}

export function isCategoryProductList (category?: { level: number }) {
  return (category?.level ?? 0) >= 2;
}

export function colorModelToHex (color: { hex: string, alpha: number }) {
  const hex = color.hex;
  const alpha = color.alpha;
  return `${hex}${(Math.round(255 * alpha)).toString(16)}`;
}

export function getLocaleNum (locale: string, num: number) {
  if (!numberFormatters[locale]) {
    numberFormatters[locale] = new Intl.NumberFormat(locale);
  }
  return numberFormatters[locale].format(num);
}

export function isOver18 (
  dateDescription: ConstructorParameters<DateConstructor>[0],
): boolean {
  return isOver18AsObj({
    dateValue: dateDescription,
    currentDate: Date.now(),
  });
}

export function isOver18AsObj (dates: {
  dateValue: ConstructorParameters<DateConstructor>[0],
  currentDate: ConstructorParameters<DateConstructor>[0],
}): boolean {
  const { dateValue, currentDate } = dates;
  const dateOfAdulthood = new Date(dateValue);
  dateOfAdulthood.setFullYear(dateOfAdulthood.getFullYear() + 18);
  return dateOfAdulthood <= new Date(currentDate);
}

export async function crossPlatformHashNotExceptionSafe (args: {
  hashFunction?: CrossPlatformHashFunction,
  hashableStr?: string,
} = {}) {
  const { hashFunction, hashableStr } = args;
  if (typeof hashableStr !== 'string') {
    throw new TypeError(
      'Provided hashableStr is not string.',
    );
  }
  // Based on: https://stackoverflow.com/questions/55926281/how-do-i-hash-a-string-using-javascript-with-sha512-algorithm#answer-55926440
  try {
    let hashFunctionModule = '';
    let hashFunctionSubtle = '';
    switch (hashFunction) {
      case HASH_SHA_256: {
        hashFunctionModule = 'sha256';
        hashFunctionSubtle = 'SHA-256';
      }
        break;
      case HASH_SHA_512: {
        hashFunctionModule = 'sha512';
        hashFunctionSubtle = 'SHA-512';
      }
        break;
      default: {
        throw new TypeError(
          'Provided hashFunction has no implementation.',
        );
      }
    }
    let cryptoLib;
    if (!import.meta.client) {
      // https://nodejs.org/docs/latest-v18.x/api/crypto.html#determining-if-crypto-support-is-unavailable
      // When using ESM, if there is a chance that the code may be run on a
      // build of Node.js where crypto support is not enabled, consider using
      // the import() function instead of the lexical import keyword.
      cryptoLib = await import('node:crypto');
      return {
        error: false,
        value: (cryptoLib.createHash(hashFunctionModule)
          .update(hashableStr)
          .digest('hex')
        ),
      };
    } else {
      cryptoLib = crypto;
    }
    if (cryptoLib.subtle) {
      const buf = await cryptoLib.subtle.digest(
        hashFunctionSubtle,
        (new TextEncoder()
          .encode(hashableStr)
        ),
      );
      return {
        error: false,
        value: (Array.prototype.map.call(
          new Uint8Array(buf),
          x => (('00' + x.toString(16)).slice(-2)),
        )
          .join('')
        ),
      };
    } else {
      throw new Error(
        'crypto methods are unavailable on this platform. This may be due to crypto\'s methods only available on https protocol or localhost domain or nodejs.',
      );
    }
  } catch (error) {
    return {
      error,
      value: '',
    };
  }
}

/* > When the time zone offset is absent, date-only forms are interpreted
 *   as a UTC time and date-time forms are interpreted as local time.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
 * Outputs the format YYYY-MM-DDThh:mm:ss which then can be parsed as local
 * time independently from the original timezone This is useful for timezone
 * independent dates like birth dates.
*/
export function removeTZInfoFromDateStr (dateStr?: string) {
  return dateStr?.slice(0, 19);
}

export function base64Decode (encoded: string) {
  return import.meta.server ? Buffer.from(encoded, 'base64') : window.atob(encoded);
}

export function roundToDigits (digits: number, num: string | number) {
  const multiplier = 10 ** digits;
  let result = Math.floor(Number(num) * multiplier) / multiplier;

  if (digits < 0) {
    result = Math.round(result);
  }
  return result;
}

export function hasProperty (object: unknown, oKey: string) {
  return object && Object.prototype.hasOwnProperty.call(object, oKey);
}
